import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, CheckCircle2, XCircle, Star, ShieldCheck, Instagram, Facebook, Youtube } from "lucide-react";
import { Reveal, SectionHeading } from "./primitives";
import { Icon } from "./sections";
import { SITE, IMAGES, DNA_JOURNEY, FRAMEWORK, DPR_AREAS, PERSONALIZED_STEPS, TRAINER_STEPS, DIFFERENT_PATHS, RTO_SERVICES, FEATURES7, VIDEOS } from "../data/site";
import { tr } from "../lib/language";

export const SevenFeatures = () => (
  <section className="bg-dwc-soft py-24 md:py-32" data-testid="seven-features-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading
        eyebrow="The Seven DWC Commitments"
        number="10"
        title="Seven Things Every DWC Learner Gets."
        text="Each commitment stands on its own — clear, specific and honoured under the applicable DWC terms."
      />
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {FEATURES7.map((f, i) => (
          <Reveal key={tr(f.title)} delay={(i % 4) * 0.07} className="h-full">
            <article data-testid={`feature7-card-${i}`} className={`group flex h-full flex-col rounded-2xl border p-7 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 ${i === 0 ? "border-dwc-green/30 bg-dwc-green text-white grain" : "border-dwc-green/10 bg-white hover:shadow-[0_20px_50px_rgb(23,107,58,0.10)]"}`}>
              <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${i === 0 ? "bg-white/15 text-white" : "bg-dwc-soft text-dwc-green group-hover:bg-dwc-green group-hover:text-white"} transition-colors duration-300`}>
                <Icon name={f.icon} className="h-5 w-5" />
              </span>
              <h3 className={`mt-4 text-base font-extrabold tracking-tight ${i === 0 ? "text-white" : "text-dwc-charcoal"}`}>{tr(f.title)}</h3>
              <p className={`mt-2 flex-1 text-[13px] leading-relaxed ${i === 0 ? "text-white/85" : "text-dwc-charcoal/65"}`}>{tr(f.text)}</p>
              {f.to && (
                <Link to={f.to} className={`mt-4 inline-flex items-center gap-1.5 text-[12px] font-extrabold uppercase tracking-wide ${i === 0 ? "text-emerald-200" : "text-dwc-green"}`}>
                  {tr("Know More")} <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              )}
            </article>
          </Reveal>
        ))}
        <Reveal delay={0.3} className="h-full">
          <div className="flex h-full flex-col justify-center rounded-2xl border-2 border-dashed border-dwc-green/30 bg-white/60 p-7" data-testid="feature7-note">
            <p className="text-sm font-extrabold uppercase tracking-wide text-dwc-green">{tr("One Honest Note")}</p>
            <p className="mt-2 text-[13px] leading-relaxed text-dwc-charcoal/70">{tr("Training duration is personalised — minimum 11-day package, up to the 33-day structured framework — decided after your DPR assessment, never as a fixed 7-day promise.")}</p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const VideoStories = () => {
  const [videos, setVideos] = useState([]);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/store/stories`)
      .then((r) => r.json())
      .then((list) => setVideos((Array.isArray(list) ? list : []).filter((m) => m.media_type === "review" && m.media_url).map((m) => m.media_url)))
      .catch(() => {});
  }, []);
  if (videos.length === 0) return null;
  return (
    <section className="bg-white py-24 md:py-32" data-testid="video-stories-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          eyebrow="Client Review Videos"
          number="03"
          title="Hear It From Real DWC Students."
          text="Genuine video moments from DWC training and student journeys in Mumbai, Surat & Ahmedabad — no actors, no scripts."
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {videos.slice(0, 3).map((src, i) => (
            <Reveal key={src} delay={i * 0.1}>
              <video
                src={src}
                controls
                preload="metadata"
                playsInline
                onError={(e) => { e.currentTarget.closest("div").style.display = "none"; }}
                className="max-h-[480px] w-full rounded-2xl border border-gray-100 bg-dwc-charcoal object-contain shadow-[0_8px_30px_rgb(0,0,0,0.08)]"
                data-testid={`home-video-story-${i}`}
              />
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-12 text-center">
            <Link to="/success-stories?tab=reviews" data-testid="video-stories-all-btn" className="group inline-flex items-center gap-2 rounded-xl border-2 border-dwc-green px-7 py-4 text-sm font-bold uppercase tracking-wide text-dwc-green transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-dwc-soft">
              {tr("View More Testimonials")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
            <p className="mt-3 text-sm text-dwc-charcoal/60" data-testid="video-stories-support-line">{tr("Read and listen — More testimonials from our real customers.")}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const SkillsVsConfidence = () => (
  <section className="bg-white py-24 md:py-32" data-testid="skills-vs-confidence-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading
        eyebrow="Skills vs Confidence"
        number="02"
        title="Learning To Operate A Car And Becoming A Confident Driver Are Not The Same Thing."
        text="A learner may know all the controls — and still freeze in real traffic. That gap is exactly what DWC trains."
      />
      <div className="mt-14 grid gap-6 lg:grid-cols-2">
        <Reveal className="h-full">
          <article className="h-full rounded-3xl border border-gray-100 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:p-10" data-testid="skills-card">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-dwc-charcoal/50">A learner may know</p>
            <h3 className="mt-3 text-2xl font-extrabold tracking-tight text-dwc-charcoal">Driving Skills</h3>
            <ul className="mt-6 grid grid-cols-2 gap-3">
              {["Steering", "Brake", "Accelerator", "Clutch", "Gear", "Indicators"].map((s) => (
                <li key={s} className="flex items-center gap-2 rounded-xl bg-dwc-soft px-4 py-3 text-sm font-bold text-dwc-charcoal/80">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-dwc-green" aria-hidden="true" />{s}
                </li>
              ))}
            </ul>
          </article>
        </Reveal>
        <Reveal delay={0.12} className="h-full">
          <article className="h-full rounded-3xl border border-dwc-crimson/20 bg-white p-8 shadow-[0_8px_30px_rgba(181,18,43,0.06)] md:p-10" data-testid="confidence-card">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-dwc-crimson">But still experience</p>
            <h3 className="mt-3 text-2xl font-extrabold tracking-tight text-dwc-charcoal">Missing Confidence</h3>
            <ul className="mt-6 grid grid-cols-2 gap-3">
              {["Fear and self-doubt", "Traffic fear", "Freezing under pressure", "Late decisions", "Mirror neglect", "Panic braking", "Depending on the trainer", "Avoiding driving alone"].map((s) => (
                <li key={s} className="flex items-center gap-2 rounded-xl bg-dwc-crimson/[0.05] px-4 py-3 text-sm font-bold text-dwc-charcoal/80">
                  <XCircle className="h-4 w-4 shrink-0 text-dwc-crimson" aria-hidden="true" />{s}
                </li>
              ))}
            </ul>
          </article>
        </Reveal>
      </div>
    </div>
  </section>
);

export const LessonSystem = () => (
  <section className="relative overflow-hidden bg-dwc-green py-24 md:py-32 grain" data-testid="lesson-system-section">
    <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/10" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <SectionHeading
          dark
          eyebrow="The DWC Methodology"
          number="04"
          title="The 33-Lesson System"
          text="Instead of giving every learner the same routine, DWC follows a structured training system designed to progressively build driving skills, reaction and confidence."
        />
        <Reveal delay={0.15}>
          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center" data-testid="lesson-system-visual">
            {[
              { big: "33", small: "Lessons" },
              { big: "3", small: "DNA Levels" },
              { big: "1", small: "Personalized Plan" },
            ].map((b, i) => (
              <div key={b.small} className="flex flex-1 items-center gap-3 sm:flex-col sm:gap-0">
                <div className="flex-1 rounded-2xl border border-white/15 bg-white/10 p-6 text-center backdrop-blur-sm sm:w-full">
                  <p className="text-4xl font-black tracking-tight text-white md:text-5xl">{b.big}</p>
                  <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-200">{b.small}</p>
                </div>
                {i < 2 && <span className="hidden text-2xl font-black text-white/50 sm:block sm:py-2">+</span>}
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-xs font-bold uppercase tracking-[0.3em] text-white/60">
            Structured · Progressive · Personalized · Confidence-Oriented
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

export const DnaJourneyStrip = () => (
  <Reveal delay={0.15}>
    <div className="mt-12 flex flex-wrap items-center justify-center gap-3" data-testid="dna-journey-strip">
      {DNA_JOURNEY.map((step, i) => (
        <span key={step} className="flex items-center gap-3">
          <span className={`rounded-full px-6 py-2.5 text-sm font-extrabold uppercase tracking-[0.15em] ${i === DNA_JOURNEY.length - 1 ? "bg-dwc-green text-white" : "border border-dwc-green/30 bg-white text-dwc-green"}`}>
            {step}
          </span>
          {i < DNA_JOURNEY.length - 1 && <ArrowRight className="h-4 w-4 text-dwc-crimson" aria-hidden="true" />}
        </span>
      ))}
    </div>
  </Reveal>
);

export const DprSection = () => (
  <section className="bg-white py-24 md:py-32" data-testid="dpr-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr]">
        <div>
          <SectionHeading
            eyebrow="DPR"
            number="06"
            title="Driving Psychology Report"
            text="Every learner is different. DPR helps us understand the learner's current driving behaviour, confidence and areas that may need improvement."
          />
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap items-center gap-3 rounded-2xl bg-dwc-soft p-5">
              {["High", "Medium", "Low"].map((l) => (
                <span key={l} className={`rounded-full px-4 py-1.5 text-xs font-extrabold uppercase tracking-wider ${l === "High" ? "bg-dwc-green text-white" : l === "Medium" ? "border border-dwc-green/40 text-dwc-green" : "border border-dwc-crimson/40 text-dwc-crimson"}`}>{l}</span>
              ))}
              <span className="min-w-0 text-xs font-semibold text-dwc-charcoal/60">Levels observed per area</span>
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <ul className="mt-6 space-y-2.5" data-testid="dpr-practical-details">
              {["A practical assessment of about 1–1.5 hours, conducted in your own car", "Covers learning, reaction, confidence and driving-psychology aspects", "Same-day assessment when an instructor slot is available — otherwise on a suitable day", "Your personalised training recommendation comes only after this assessment"].map((d) => (
                <li key={d} className="flex items-start gap-2.5 text-[13px] font-semibold text-dwc-charcoal/75">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-dwc-crimson" aria-hidden="true" />{d}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-6 text-xs leading-relaxed text-dwc-charcoal/50" data-testid="dpr-disclaimer">
              DPR is a driving-learning, behavioural and confidence assessment framework. It is not a medical or psychiatric diagnosis.
            </p>
          </Reveal>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {DPR_AREAS.map((a, i) => (
            <Reveal key={tr(a.title)} delay={(i % 2) * 0.08} className="h-full">
              <article data-testid={`dpr-area-${i}`} className="h-full rounded-2xl border border-gray-100 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(23,107,58,0.10)]">
                <p className="text-xs font-black tracking-[0.2em] text-dwc-crimson">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-2 text-base font-extrabold tracking-tight text-dwc-charcoal">{tr(a.title)}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-dwc-charcoal/65">{tr(a.text)}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const PersonalizedSection = () => (
  <section className="bg-dwc-soft py-24 md:py-32" data-testid="personalized-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading
        eyebrow="Personalized Learning"
        number="07"
        title="Not Every Learner Is The Same."
        text="Learners differ in skill, confidence, fear, learning speed and driving requirements. So training should fit the learner — not the other way around."
      />
      <div className="mt-14 grid gap-4 md:grid-cols-5">
        {PERSONALIZED_STEPS.map((s, i) => (
          <Reveal key={tr(s.title)} delay={i * 0.08} className="h-full">
            <div className="relative h-full rounded-2xl border border-dwc-green/10 bg-white p-6" data-testid={`personalized-step-${i}`}>
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-dwc-green text-xs font-black text-white">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-4 text-sm font-extrabold uppercase tracking-wide text-dwc-charcoal">{tr(s.title)}</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-dwc-charcoal/65">{tr(s.text)}</p>
              {i < PERSONALIZED_STEPS.length - 1 && (
                <ArrowRight className="absolute -right-4 top-1/2 hidden h-4 w-4 -translate-y-1/2 text-dwc-crimson md:block" aria-hidden="true" />
              )}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const TrainerIndependence = () => (
  <section className="bg-white py-24 md:py-32" data-testid="trainer-independence-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading
        eyebrow="The DWC Philosophy"
        number="08"
        title="From Trainer Support To Independent Driving."
        text="Good training should gradually move the learner from depending on the trainer to taking control and making confident driving decisions independently."
      />
      <div className="mt-14 flex flex-col gap-4 lg:flex-row lg:items-stretch">
        {TRAINER_STEPS.map((s, i) => (
          <Reveal key={tr(s.title)} delay={i * 0.1} className="flex-1">
            <div className="flex h-full items-center gap-5 rounded-2xl border border-gray-100 bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`trainer-step-${i}`}>
              <span className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-lg font-black ${i === 2 ? "bg-dwc-green text-white" : "bg-dwc-soft text-dwc-green"}`}>{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h3 className="text-base font-extrabold uppercase tracking-wide text-dwc-charcoal">{tr(s.title)}</h3>
                <p className="mt-1 text-sm leading-relaxed text-dwc-charcoal/65">{tr(s.text)}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const DifferentSection = () => (
  <section className="bg-dwc-charcoal py-24 md:py-32 grain" data-testid="different-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading
        dark
        eyebrow="Why DWC Is Different"
        number="09"
        title="This Is Not An Ordinary Driving School."
        text="A normal driving school may focus mainly on teaching vehicle operation. DWC focuses on the complete journey to independent, confident driving."
      />
      <div className="mt-14 grid gap-6 lg:grid-cols-2">
        <Reveal className="h-full">
          <article className="h-full rounded-3xl border border-white/10 bg-white/5 p-8" data-testid="traditional-path">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/50">Traditional Approach</p>
            <div className="mt-6 space-y-3">
              {DIFFERENT_PATHS.traditional.map((s, i) => (
                <div key={s} className="flex items-center gap-3">
                  <span className="text-sm font-bold text-white/60">{s}</span>
                  {i < DIFFERENT_PATHS.traditional.length - 1 && <ArrowDown className="h-3.5 w-3.5 text-white/30" aria-hidden="true" />}
                </div>
              ))}
            </div>
          </article>
        </Reveal>
        <Reveal delay={0.12} className="h-full">
          <article className="h-full rounded-3xl border border-dwc-green/40 bg-dwc-green/15 p-8" data-testid="dwc-path">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-300">The DWC Approach</p>
            <div className="mt-6 flex flex-wrap items-center gap-2.5">
              {DIFFERENT_PATHS.dwc.map((s, i) => (
                <span key={s} className="flex items-center gap-2.5">
                  <span className="rounded-full border border-emerald-300/40 bg-dwc-green/30 px-4 py-2 text-sm font-extrabold text-white">{s}</span>
                  {i < DIFFERENT_PATHS.dwc.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-dwc-crimson" aria-hidden="true" />}
                </span>
              ))}
            </div>
          </article>
        </Reveal>
      </div>
      <Reveal delay={0.2}>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-2.5" data-testid="framework-chips">
          {FRAMEWORK.map((f, i) => (
            <span key={f} className="flex items-center gap-2.5">
              <span className="rounded-lg bg-white/10 px-4 py-2 text-xs font-extrabold uppercase tracking-wider text-white/85">{f}</span>
              {i < FRAMEWORK.length - 1 && <span className="text-dwc-crimson">+</span>}
            </span>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export const RealDriving = () => (
  <section className="bg-white py-24 md:py-32" data-testid="real-driving-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <div>
          <SectionHeading
            eyebrow="Real Driving Training"
            number="10"
            title="Real Driving. Real Roads. Real Traffic. Real Decisions."
            text="Driving confidence develops through real practice and real road situations — not just empty grounds."
          />
          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {["Real vehicle practice", "Real traffic", "Road awareness", "Practical control", "Parking practice", "Highway driving", "Challenging road situations", "Real manual driving practice for RTO"].map((s) => (
              <li key={s} className="flex items-start gap-2.5 text-sm font-semibold text-dwc-charcoal/80">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-dwc-green" aria-hidden="true" />{s}
              </li>
            ))}
          </ul>
        </div>
        <Reveal delay={0.2}>
          <div className="relative">
            <img src={IMAGES.dwcRoad} alt="Learner practising on a real road in city traffic" loading="lazy" className="aspect-[4/3] w-full rounded-[2rem] object-cover shadow-[0_24px_60px_rgb(23,107,58,0.15)]" />
            <div className="absolute -bottom-4 -right-4 h-full w-full rounded-[2rem] border-2 border-dwc-green/15" aria-hidden="true" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const RtoSupport = () => (
  <section className="bg-dwc-soft py-24 md:py-32" data-testid="rto-support-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <SectionHeading
          eyebrow="RTO Support Ecosystem"
          number="13"
          title="More Than Driving Training."
          text="Learners can continue to approach DWC for RTO-related guidance within the scope of the service."
        />
        <Reveal delay={0.2}>
          <Link to="/rto-consulting" data-testid="rto-support-link" className="group inline-flex shrink-0 items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-dwc-green transition-colors hover:text-dwc-green-dark">
            Explore RTO Support
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {RTO_SERVICES.slice(0, 4).map((s, i) => (
          <Reveal key={tr(s.title)} delay={(i % 4) * 0.08} className="h-full">
            <article data-testid={`rto-highlight-${i}`} className="h-full rounded-2xl border border-dwc-green/10 bg-white p-7 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(23,107,58,0.10)]">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-dwc-soft text-dwc-green">
                <Icon name={s.icon} className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-base font-extrabold tracking-tight text-dwc-charcoal">{tr(s.title)}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-dwc-charcoal/65">{tr(s.text)}</p>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const OwnCarSection = () => (
  <section className="bg-white py-24 md:py-32" data-testid="own-car-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <div>
          <SectionHeading
            eyebrow="Own-Car Training"
            number="09B"
            title="Learn In Your Own Car."
            text="Learn in the car you will actually drive. DWC trains you in your own vehicle — Manual, Automatic or EV, any suitable brand or model."
          />
          <div className="mt-6 flex flex-wrap gap-2">
            {["Manual", "Automatic", "EV"].map((t) => (
              <span key={t} className="rounded-full bg-dwc-green px-5 py-2 text-xs font-extrabold uppercase tracking-[0.15em] text-white">{t}</span>
            ))}
          </div>
          <ul className="mt-7 grid gap-3 sm:grid-cols-2">
            {["Familiarity with your actual vehicle", "Real-world driving from day one", "Know your car's steering, braking and response", "No transition anxiety after training", "Greater confidence and independence", "Free doorstep pickup & drop across Mumbai, Surat & Ahmedabad"].map((s) => (
              <li key={s} className="flex items-start gap-2.5 text-sm font-semibold text-dwc-charcoal/80">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-dwc-green" aria-hidden="true" />{s}
              </li>
            ))}
          </ul>
          <Reveal delay={0.15}>
            <p className="mt-6 rounded-xl border border-dwc-crimson/20 bg-dwc-crimson/[0.04] p-4 text-[13px] font-semibold text-dwc-charcoal/75" data-testid="own-car-note">
              <span className="font-extrabold text-dwc-crimson">Important:</span> DWC provides training in the learner's own car. Please keep your own suitable vehicle available for training before booking an appointment.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <div className="rounded-[2rem] bg-dwc-charcoal p-8 text-white md:p-10 grain">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-300">The DWC Philosophy</p>
            <div className="mt-6 space-y-4">
              {["Instructor Guidance", "Learner Control", "Independent Confidence"].map((s, i) => (
                <div key={s} className="flex items-center gap-4">
                  <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-sm font-black ${i === 2 ? "bg-dwc-green text-white" : "bg-white/10 text-emerald-200"}`}>{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-base font-extrabold uppercase tracking-wide">{s}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm leading-relaxed text-white/70">Expert, experienced instructors guide you step by step until you take full, confident control of your own car — with free home pickup and drop across Mumbai, Surat & Ahmedabad.</p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const SocialProof = () => (
  <section className="bg-white py-20 md:py-24" data-testid="social-proof-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <Reveal>
        <div className="rounded-[2rem] border border-gray-100 bg-white p-8 shadow-[0_16px_50px_rgb(0,0,0,0.06)] md:p-12">
          <div className="flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-dwc-green">15 — Trusted Across Mumbai, Surat & Ahmedabad</p>
              <div className="mt-4 flex items-center gap-4">
                <span className="text-5xl font-black tracking-tight text-dwc-charcoal">{SITE.googleRating}</span>
                <span>
                  <span className="flex gap-0.5" aria-label={`Rated ${SITE.googleRating} out of 5 on Google`}>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} className="h-5 w-5 fill-dwc-green text-dwc-green" aria-hidden="true" />
                    ))}
                  </span>
                  <span className="mt-1 block text-sm font-bold text-dwc-charcoal/70">{SITE.googleReviewCount} Google reviews</span>
                </span>
              </div>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-dwc-charcoal/65">
                Real ratings from real learners — see what students say about DWC on Google.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <a href={SITE.googleReview} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-link" className="inline-flex items-center gap-2 rounded-xl bg-dwc-green px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-dwc-green-dark">
                <ShieldCheck className="h-4 w-4" aria-hidden="true" /> Read Google Reviews
              </a>
              <a href={SITE.instagram} target="_blank" rel="noopener noreferrer" data-testid="instagram-link" aria-label="DWC on Instagram" className="rounded-xl border-2 border-dwc-green/25 p-3 text-dwc-green transition-colors hover:bg-dwc-soft">
                <Instagram className="h-5 w-5" aria-hidden="true" />
              </a>
              <a href={SITE.facebook} target="_blank" rel="noopener noreferrer" data-testid="facebook-link" aria-label="DWC on Facebook" className="rounded-xl border-2 border-dwc-green/25 p-3 text-dwc-green transition-colors hover:bg-dwc-soft">
                <Facebook className="h-5 w-5" aria-hidden="true" />
              </a>
              <a href={SITE.youtube} target="_blank" rel="noopener noreferrer" data-testid="youtube-link" aria-label="DWC on YouTube" className="rounded-xl border-2 border-dwc-green/25 p-3 text-dwc-green transition-colors hover:bg-dwc-soft">
                <Youtube className="h-5 w-5" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
